import api from '../../../services/api';
import { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import { useForm } from "react-hook-form";
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import { Button } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PageContainer from '../../../common/components/PageContainer';
import useAlerta from '../../../common/components/useAlerta';
import AppSnackbar from '../../../common/components/AppSnackbar';
import PermissoesPicker from '../../../common/components/PermissoesPicker';
import usePermissoes from '../../../common/hooks/usePermissoes';
import Permissoes from '../../../common/permissoes';
import { Checkbox, FormControlLabel } from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';

function FuncionarioItem() {
    const { id } = useParams();
    const [imageData, setImageData] = useState(null);
    const { alerta, mostrarAlerta, fecharAlerta } = useAlerta();
    const { register, handleSubmit, reset } = useForm();
    const { isTI, hasPermission } = usePermissoes();
    const podeEditar = hasPermission(Permissoes.FUNCIONARIO_CADASTRO);
    const [permissoes, setPermissoes] = useState([]);
    const [setores, setSetores] = useState([]);
    const [exibirPermissoes, setExibirPermissoes] = useState(false);
    const [exibirSetores, setExibirSetores] = useState(false);
    const [permissoesSelecionadas, setPermissoesSelecionadas] = useState([]);
    const [setorSelecionado, setSetorSelecionado] = useState("");

    const handleCheckboxChange = (permissaoId) => {
        setPermissoesSelecionadas((prevState) =>
            prevState.includes(permissaoId)
                ? prevState.filter((id) => id !== permissaoId)
                : [...prevState, permissaoId]
        );
    };

    const handleToggleGrupo = (idsGrupo, marcar) => {
        setPermissoesSelecionadas((prevState) =>
            marcar
                ? Array.from(new Set([...prevState, ...idsGrupo]))
                : prevState.filter((id) => !idsGrupo.includes(id))
        );
    };

    useEffect(() => {
        async function fetchData() {
            try {
                const response = await api.get(`/permissao`);
                setPermissoes(response.data);
                const responseSetor = await api.get(`/setor`);
                setSetores(responseSetor.data);
            } catch (error) {
                console.error('Erro ao buscar as informações', error);
            }
        }
        fetchData();
    }, []);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            convertToBase64(file);
        }
    };

    const convertToBase64 = (file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
            setImageData(reader.result);
        };
        reader.onerror = (error) => {
            console.error('Erro ao converter a imagem para Base64:', error);
        };
    };

    const handleCheckboxChangeSetor = (setorId) => {
        setSetorSelecionado(setorId);
    };

    const onSubmit = async (data) => {
        if (!podeEditar) return;

        if (!imageData) {
            console.error('Nenhuma imagem foi selecionada.');
            return;
        }

        const formData = {
            ...data,
            foto: imageData,
            permissoes: permissoesSelecionadas,
            setor: setorSelecionado,
        };

        try {
            await api.put(`/funcionario/${id}`, formData);
            mostrarAlerta('Funcionário atualizado com sucesso!', 'success');
            setTimeout(() => {
                window.location.href = '/funcionario';
            }, 1500);
        } catch (error) {
            mostrarAlerta('Erro ao atualizar o funcionário!', 'error');
        }
        setTimeout(fecharAlerta, 3000);
    };

    useEffect(() => {
        async function fetchData() {
            try {
                const response = await api.get(`/funcionario/${id}`);
                const funcionarioData = response.data;

                const formatDate = (dateString) => {
                    if (!dateString) return '';
                    return dateString.split('T')[0];
                };

                reset({
                    ...funcionarioData,
                    data_aniversario: formatDate(funcionarioData.data_aniversario),
                });

                if (funcionarioData.permissoes) {
                    setPermissoesSelecionadas(funcionarioData.permissoes);
                }

                if (funcionarioData.setor) {
                    setSetorSelecionado(funcionarioData.setor.id);
                }

                if (response.data.foto) {
                    setImageData(`data:image/jpeg;base64,${response.data.foto}`);
                }
            } catch (error) {
                console.error('Erro ao buscar o funcionário:', error);
            }
        }
        fetchData();
    }, [id, reset]);

    return (
        <PageContainer sx={{ maxWidth: 900 }}>
            <Paper component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: { xs: 2, sm: 4 } }}>
                <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                    {podeEditar ? 'Atualização de Funcionário' : 'Dados do Funcionário'}
                </Typography>

                {!podeEditar && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Você está vendo estes dados em modo somente leitura. Fale com o time de TI se precisar editá-los.
                    </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <Avatar src={imageData || undefined} sx={{ width: 96, height: 96, bgcolor: 'primary.light' }}>
                        {!imageData && <PersonOutlineIcon fontSize="large" />}
                    </Avatar>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <TextField {...register("nome")} label="Nome completo" placeholder="Digite o nome..." required fullWidth InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !podeEditar }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField {...register("nRegistro")} label="Nº Registro" placeholder="Digite o número de registro..." fullWidth InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !podeEditar }} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField {...register("dataAdmissao")} label="Data de Admissão" type="date" InputLabelProps={{ shrink: true }} fullWidth InputProps={{ readOnly: !podeEditar }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField {...register("dataDemissao")} label="Data de Demissão" type="date" InputLabelProps={{ shrink: true }} fullWidth InputProps={{ readOnly: !podeEditar }} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField {...register("telefone")} label="Telefone" type="tel" placeholder="Digite o telefone..." fullWidth InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !podeEditar }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField {...register("cpf")} label="CPF" placeholder="Digite o CPF..." fullWidth InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !podeEditar }} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField {...register("cargo")} label="Cargo" placeholder="Digite o cargo..." fullWidth InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !podeEditar }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField {...register("data_aniversario")} label="Data de nascimento" type="date" InputLabelProps={{ shrink: true }} fullWidth InputProps={{ readOnly: !podeEditar }} />
                    </Grid>

                    <Grid item xs={6} sm={4}>
                        <TextField {...register("tamCamisa")} label="Tamanho da Camisa" placeholder="Digite o tamanho..." fullWidth InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !podeEditar }} />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <TextField {...register("tamCalca")} label="Tamanho da Calça" placeholder="Digite o tamanho..." fullWidth InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !podeEditar }} />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <TextField {...register("nCalcado")} label="Nº Calçado" type="number" fullWidth InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !podeEditar }} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField {...register("salario")} label="Salário" fullWidth InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !podeEditar }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField {...register("email")} label="Email" type="email" placeholder="Digite o email..." required fullWidth InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !podeEditar }} />
                    </Grid>

                    {podeEditar && (
                        <Grid item xs={12}>
                            <Button variant="outlined" component="label" fullWidth>
                                Selecionar nova foto
                                <input
                                    {...register("foto")}
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={handleFileChange}
                                />
                            </Button>
                        </Grid>
                    )}
                </Grid>

                {isTI && podeEditar && (
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setExibirPermissoes(!exibirPermissoes)}
                        sx={{ mt: 3 }}
                    >
                        {exibirPermissoes ? "Ocultar Permissões" : "Selecionar Permissões"}
                    </Button>
                )}

                {isTI && podeEditar && exibirPermissoes && (
                    <Box sx={{ mt: 1 }}>
                        <PermissoesPicker
                            permissoesDisponiveis={permissoes}
                            selecionadas={permissoesSelecionadas}
                            onToggle={handleCheckboxChange}
                            onToggleGrupo={handleToggleGrupo}
                        />
                    </Box>
                )}

                {podeEditar && (
                    <Box>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setExibirSetores(!exibirSetores)}
                            sx={{ mt: 2 }}
                        >
                            {exibirSetores ? "Ocultar Setores" : "Selecionar Setor"}
                        </Button>
                    </Box>
                )}

                {podeEditar && exibirSetores && (
                    <Box sx={{ mt: 1 }}>
                        {setores.map((setor) => (
                            <FormControlLabel
                                key={setor.id}
                                control={
                                    <Checkbox
                                        icon={<CheckBoxOutlineBlank fontSize="small" />}
                                        checkedIcon={<CheckBox fontSize="small" />}
                                        checked={setorSelecionado === setor.id}
                                        onChange={() => handleCheckboxChangeSetor(setor.id)}
                                        color="secondary"
                                    />
                                }
                                label={setor.nome}
                            />
                        ))}
                    </Box>
                )}

                {podeEditar && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <Button type="submit" variant="contained" color="secondary" size="large" sx={{ px: 6 }}>
                            Atualizar
                        </Button>
                    </Box>
                )}
            </Paper>

            <AppSnackbar alerta={alerta} onClose={fecharAlerta} />
        </PageContainer>
    );
}

export default FuncionarioItem;
